import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../features/payment/hooks/usePayment';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

const PaymentPage: React.FC = () => {
  const { t } = useTranslation('common');
  const {
    user,
    loading,
    qrAmount,
    setQrAmount,
    transferContent,
    showQR,
    totalDeposited,
    showSuccessAnimation,
    successAmount,
    qrError,
    qrLoading,
    setQrError,
    setQrLoading,
    currentOrder,
    formatCurrency,
    createOrder,
    createManualOrder,
    BANK_INFO,
    getQRCodeUrl
  } = usePayment();

  const [manualAmount, setManualAmount] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [manualLoading, setManualLoading] = useState(false);

  const handleManualSubmit = async () => {
    const amt = parseFloat(manualAmount.replace(/,/g, ''));
    if (!amt) return;
    setManualLoading(true);
    const success = await createManualOrder(amt, manualContent, manualNote);
    if (success) {
      setManualAmount('');
      setManualContent('');
      setManualNote('');
    }
    setManualLoading(false);
  };

  if (!user) return <div className="p-8 text-center">{t('payment.loginRequired', { defaultValue: 'Please log in to access this page' })}</div>;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h1 className="text-3xl font-bold text-gray-900">{t('payment.title', { defaultValue: 'Payment & Top-up' })}</h1>
          <Card className="w-full md:w-auto bg-green-50 border-green-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div>
                <p className="text-sm font-medium text-green-800">{t('payment.balanceLoaded', { defaultValue: 'Total Deposited' })}</p>
                <p className={`text-2xl font-bold text-green-700 ${showSuccessAnimation ? 'animate-pulse' : ''}`}>
                  {formatCurrency(totalDeposited)}
                </p>
              </div>
              {showSuccessAnimation && <Badge className="bg-green-600">+{formatCurrency(successAmount)}</Badge>}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Customer Info */}
          <Card className="md:col-span-1 h-fit">
            <CardHeader>
              <CardTitle className="text-lg">{t('payment.customerInfo', { defaultValue: 'Customer Info' })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('payment.name', { defaultValue: 'Name' })}</span>
                <span className="font-medium">{user.full_name || user.username}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">ID</span>
                <span className="font-medium">{user.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Email</span>
                <span className="font-medium truncate max-w-[150px]" title={user.email}>{user.email}</span>
              </div>
            </CardContent>
          </Card>

          {/* Payment Tabs */}
          <div className="md:col-span-2">
            <Tabs defaultValue="auto">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="auto">{t('payment.tabs.auto', { defaultValue: 'Auto Payment (QR)' })}</TabsTrigger>
                <TabsTrigger value="manual">{t('payment.tabs.manual', { defaultValue: 'Manual Transfer' })}</TabsTrigger>
              </TabsList>

              <TabsContent value="auto" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('payment.auto.createQR', { defaultValue: 'Create QR Code' })}</CardTitle>
                    <CardDescription>{t('payment.auto.desc', { defaultValue: 'Scan QR with your banking app for instant top-up.' })}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-4 items-end">
                      <div className="flex-1 space-y-2">
                        <label className="text-sm font-medium">{t('payment.amount', { defaultValue: 'Amount (VND)' })}</label>
                        <Input
                          value={qrAmount}
                          onChange={(e) => setQrAmount(e.target.value)}
                          placeholder="10,000"
                        />
                      </div>
                      <Button onClick={createOrder} disabled={loading || !qrAmount}>
                        {loading ? '...' : t('payment.createQR', { defaultValue: 'Create QR' })}
                      </Button>
                    </div>

                    {showQR && (
                      <div className="mt-6 border rounded-lg p-6 bg-white flex flex-col items-center text-center">
                        <h3 className="font-bold text-lg mb-2">{t('payment.transferInfo', { defaultValue: 'Transfer Info' })}</h3>
                        <div className="w-full max-w-sm bg-gray-50 p-4 rounded text-left space-y-2 text-sm mb-4">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Bank</span>
                            <span className="font-medium">{BANK_INFO.bank}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Account</span>
                            <span className="font-medium">{BANK_INFO.accountNumber}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Name</span>
                            <span className="font-medium">{BANK_INFO.accountName}</span>
                          </div>
                          <div className="pt-2 border-t mt-2">
                            <span className="text-gray-500 block mb-1">Content</span>
                            <code className="bg-yellow-100 px-2 py-1 rounded text-yellow-800 font-bold block">{transferContent}</code>
                          </div>
                        </div>

                        <div className="relative w-64 h-64 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                          {qrLoading && <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">Loading...</div>}
                          <img
                            src={getQRCodeUrl()}
                            alt="QR Code"
                            className="max-w-full h-auto"
                            onLoad={() => setQrLoading(false)}
                            onError={() => setQrError(true)}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-4 max-w-xs">{t('payment.auto.notes', { defaultValue: 'Payment is usually processed within 1-5 minutes.' })}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="manual" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('payment.manual.title', { defaultValue: 'Manual Transfer' })}</CardTitle>
                    <CardDescription>{t('payment.manual.desc', { defaultValue: 'Transfer manually and submit info for review.' })}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Bank Info Card (Simplified) */}
                    <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                      <p>Transfer to: Only Personal Accounts accepted.</p>
                      <strong>{BANK_INFO.bank} - {BANK_INFO.accountNumber} - {BANK_INFO.accountName}</strong>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">{t('payment.amount', { defaultValue: 'Amount (VND)' })}</label>
                      <Input value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} placeholder="Amount transferred" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">{t('payment.content', { defaultValue: 'Transfer Content' })}</label>
                      <Input value={manualContent} onChange={(e) => setManualContent(e.target.value)} placeholder="Exact content you used" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">{t('payment.note', { defaultValue: 'Note' })}</label>
                      <Input value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Optional details" />
                    </div>
                    <Button onClick={handleManualSubmit} disabled={manualLoading} className="w-full">
                      {manualLoading ? '...' : t('payment.manual.submit', { defaultValue: 'Submit for Review' })}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
